import React from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { UFOEventSource } from '@atlassian/temp-observability';
import { ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';
import { Email } from '@css-xp/graphtypes';
import { AppContent, AppPage, ErrorComponents, useFeatureToggle, useRecentlyViewed, useTitle } from '@css-xp/base';
import { fetchEmailDetails } from './queries/fetch-email-details';
import EmailPreview from './email-preview';
import './email-page.scss';

interface EmailPageProps {
  title?: string;
}

export const EmailPage = ({ title }: EmailPageProps) => {
  const { emailId } = useParams<{ emailId: string }>();
  const { loading, error, data } = useQuery(fetchEmailDetails, {
    variables: {
      email: emailId,
    },
    displayName: 'FetchEmailsForContactQuery',
    notifyOnNetworkStatusChange: true,
  });
  const { updateRecentlyViewed } = useRecentlyViewed();
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(data?.fetchEmailDetails?.subject ?? '', title);
  }

  // to track the page view of the email page
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.EMAIL_SCREEN);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          subject: data.fetchEmailDetails?.subject,
          date: data.fetchEmailDetails?.date,
        },
      });
    }
  }, [data, title]);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  if (!data) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find email with ID: ${emailId}`)}
      />
    );
  }
  const emailDetails = data.fetchEmailDetails as Email;
  return (
    <AppPage data-testid="email-pane-preview">
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.EMAIL_SCREEN,
          attributes: {
            id: emailId,
            entityName: title,
          },
        }}
      />
      <AppContent className="app-content-email">
        <EmailPreview emailDetails={emailDetails} />
      </AppContent>
    </AppPage>
  );
};
